import { useState } from "react";
import { sanitizeReviewText } from "@/lib/sanitize-html";
import IconStarFill from "@/components/icons/icon-star-fill";
import IconStarLine from "@/components/icons/icon-star-line";
import IconHeart from "@/components/icons/icon-heart";
import IconChatBubbleSquare from "@/components/icons/icon-chat-bubble-square";
import IconAnonymous from "@/components/icons/icon-anonymous";
import { getTagColorStyles } from "@/modules/tag/colors";
import type { ReviewResponse, ReviewTag, ReviewAnswer } from "../types";
import AudioPlayer from "@/components/ui/audio-player";
import FormAnswersSection from "./form-answers-section";
import VideoModal from "./video-modal";

interface ReviewDetailProps {
	review: ReviewResponse & { tags?: ReviewTag[]; audio_url?: string; answers?: ReviewAnswer[] };
}

// Read-only star rating display
function StarRatingDisplay({ rating }: { rating: number | null }) {
	if (!rating) return <span className="text-sm text-tertiary">No rating</span>;

	return (
		<div className="flex items-center gap-1">
			{[1, 2, 3, 4, 5].map((star) => (star <= rating ? <IconStarFill key={star} className="w-8 h-8 text-brand" /> : <IconStarLine key={star} className="w-8 h-8 text-gray-300" />))}
		</div>
	);
}

// Source badge display
function SourceBadge({ source }: { source: string }) {
	const getSourceInfo = (source: string) => {
		const sources: Record<string, { label: string; key: string }> = {
			manual: { label: "Manual Entry", key: "manual" },
			form: { label: "Form Submission", key: "form" },
			import: { label: "Import", key: "import" },
			twitter: { label: "Twitter / X.com", key: "twitter" },
			"x.com": { label: "Twitter / X.com", key: "twitter" },
			linkedin: { label: "LinkedIn", key: "linkedin" },
			facebook: { label: "Facebook", key: "facebook" },
			instagram: { label: "Instagram", key: "instagram" },
			google_reviews: { label: "Google Reviews", key: "google" },
			capterra: { label: "Capterra", key: "capterra" },
			trustpilot: { label: "Trustpilot", key: "trustpilot" },
			slack: { label: "Slack", key: "slack" },
			"product-hunt": { label: "Product Hunt", key: "product-hunt" },
			email: { label: "Email", key: "email" },
			other: { label: "Other", key: "fallback" },
		};
		const normalized = source.toLowerCase();
		const match = sources[normalized] || Object.entries(sources).find(([k]) => normalized.includes(k))?.[1];
		const key = match?.key || "fallback";
		const label = match?.label || source;
		return { label, icon: <img src={`/images/review-source-icons/source-${key}.svg`} alt={label} className="w-3.5 h-3.5" /> };
	};

	const { label, icon } = getSourceInfo(source);

	return (
		<span className="inline-flex items-center gap-2 px-3 py-1.5 rounded-lg bg-gray-100 text-sm text-secondary">
			<span>{icon}</span>
			<span>{label}</span>
		</span>
	);
}

// Field display component
function ReadOnlyField({ label, value, isLink = false }: { label: string; value: string | null | undefined; isLink?: boolean }) {
	return (
		<div className="space-y-1.5">
			<label className="block text-xs font-medium text-tertiary uppercase tracking-wide">{label}</label>
			{isLink && value ? (
				<a href={value} target="_blank" rel="noopener noreferrer" className="text-sm text-brand hover:underline flex items-center gap-1">
					{value}
					<svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
						<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
					</svg>
				</a>
			) : (
				<div className="text-sm text-primary">{value || "—"}</div>
			)}
		</div>
	);
}

// Format date for display
function formatDate(dateStr: string | undefined): string {
	if (!dateStr) return "—";
	const date = new Date(dateStr);
	return date.toLocaleDateString("en-US", {
		year: "numeric",
		month: "long",
		day: "numeric",
	});
}

export default function ReviewDetail({ review }: ReviewDetailProps) {
	const [videoModalOpen, setVideoModalOpen] = useState(false);
	const isTestimonial = review.type === "testimonial";
	const contentType = review.content_type || "text";

	return (
		<div className="space-y-8">
			{/* Rating & Classification Section */}
			<div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
				{/* Header */}
				<div className="flex items-start gap-3 p-6">
					<div className="w-10 h-10 bg-gray-100 rounded-full flex items-center justify-center flex-shrink-0">
						<svg className="w-5 h-5 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
							<path
								strokeLinecap="round"
								strokeLinejoin="round"
								strokeWidth={1.5}
								d="M11.049 2.927c.3-.921 1.603-.921 1.902 0l1.519 4.674a1 1 0 00.95.69h4.915c.969 0 1.371 1.24.588 1.81l-3.976 2.888a1 1 0 00-.363 1.118l1.518 4.674c.3.922-.755 1.688-1.538 1.118l-3.976-2.888a1 1 0 00-1.176 0l-3.976 2.888c-.783.57-1.838-.197-1.538-1.118l1.518-4.674a1 1 0 00-.363-1.118l-3.976-2.888c-.784-.57-.38-1.81.588-1.81h4.914a1 1 0 00.951-.69l1.519-4.674z"
							/>
						</svg>
					</div>
					<div>
						<h3 className="text-t-15 font-semibold text-primary">Rating & Classification</h3>
						<p className="text-t-13 text-tertiary mt-0.5">Star rating and review classification</p>
					</div>
				</div>

				{/* Content */}
				<div className="flex border-t border-gray-200">
					{/* Left Column - Star Rating */}
					<div className="p-6 flex items-center">
						<StarRatingDisplay rating={review.rating} />
					</div>

					{/* Divider */}
					<div className="w-px bg-gray-200" />

					{/* Right Column - Classification */}
					<div className="flex-1 p-6 flex items-center">
						<div
							className={`flex items-center gap-3 px-4 py-3 rounded-lg border ${
								isTestimonial ? "border-green-500 bg-green-50 text-green-700" : "border-blue-500 bg-blue-50 text-blue-700"
							}`}
						>
							{isTestimonial ? <IconHeart className="w-5 h-5 flex-shrink-0" solid /> : <IconChatBubbleSquare className="w-5 h-5 flex-shrink-0" solid />}
							<div>
								<div className="font-medium text-sm">{isTestimonial ? "Testimonial" : "Feedback"}</div>
								<div className="text-xs opacity-75">{isTestimonial ? "Positive feedback" : "Neutral or constructive"}</div>
							</div>
						</div>
					</div>
				</div>
			</div>

			{/* Review Information Section */}
			<div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
				{/* Header */}
				<div className="flex items-start gap-3 p-6">
					<div className="w-10 h-10 bg-gray-100 rounded-full flex items-center justify-center flex-shrink-0">
						<svg className="w-5 h-5 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
							<path
								strokeLinecap="round"
								strokeLinejoin="round"
								strokeWidth={1.5}
								d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z"
							/>
						</svg>
					</div>
					<div>
						<h3 className="text-t-15 font-semibold text-primary">Review Information</h3>
						<p className="text-t-13 text-tertiary mt-0.5">What they said about you</p>
					</div>
				</div>

				{/* Content */}
				<div className="border-t border-gray-200 p-6 space-y-5">
					{/* Video Player (for video reviews) */}
					{contentType === "video" && review.mux_playback_id && (
						<div className="space-y-1.5">
							<label className="block text-xs font-medium text-tertiary uppercase tracking-wide">Video</label>
							<div className="rounded-lg overflow-hidden border border-gray-200 bg-gray-900">
								<div className="aspect-video relative">
									{review.video_thumbnail_url ? (
										<img src={review.video_thumbnail_url} alt="Video thumbnail" className="w-full h-full object-cover" />
									) : (
										<img src={`https://image.mux.com/${review.mux_playback_id}/thumbnail.jpg?time=0`} alt="Video thumbnail" className="w-full h-full object-cover" />
									)}
									<div className="absolute inset-0 flex items-center justify-center bg-black/30">
										<button
											type="button"
											onClick={() => setVideoModalOpen(true)}
											className="w-16 h-16 rounded-full bg-white/90 flex items-center justify-center hover:bg-white transition-colors cursor-pointer"
										>
											<svg className="w-8 h-8 text-gray-600 ml-1" fill="currentColor" viewBox="0 0 24 24">
												<path d="M8 5v14l11-7z" />
											</svg>
										</button>
									</div>
								</div>
							</div>
							<VideoModal open={videoModalOpen} onOpenChange={setVideoModalOpen} playbackId={review.mux_playback_id} />
						</div>
					)}

					{/* Audio Player (for audio reviews) */}
					{contentType === "audio" && review.audio_url && (
						<div className="space-y-1.5">
							<label className="block text-xs font-medium text-tertiary uppercase tracking-wide">Audio</label>
							<AudioPlayer src={review.audio_url} initialDuration={review.audio_duration} />
						</div>
					)}

					{/* Content Text */}
					<div className="space-y-1.5">
						<label className="block text-xs font-medium text-tertiary uppercase tracking-wide">{contentType === "text" ? "Content" : "Excerpt"}</label>
						<div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
							<p className="text-sm text-primary whitespace-pre-wrap leading-relaxed" dangerouslySetInnerHTML={{ __html: sanitizeReviewText(review.content_text || "—") }} />
						</div>
					</div>

					{/* Date */}
					<ReadOnlyField label="Date" value={formatDate(review.date)} />

					{/* Source Fields */}
					<div className="flex items-center gap-8">
						<div className="space-y-1.5">
							<label className="block text-xs font-medium text-tertiary uppercase tracking-wide">Source</label>
							<div className="flex items-center gap-3">
								<SourceBadge source={review.source} />
								{review?.form ? (
									<a href={`/${review.workspace_id}/forms/${review.form.id}/edit`} className="text-sm text-brand hover:underline flex items-center gap-1">
										{review.form.title}
										<svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
											<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
										</svg>
									</a>
								) : (
									review?.form_title && (
										<div className="text-sm text-brand flex items-center gap-1">
											{review.form_title}
											<span className="opacity-50">(deleted)</span>
										</div>
									)
								)}
							</div>
						</div>
						{review.source_post_url && <ReadOnlyField label="Source URL" value={review.source_post_url} isLink />}
					</div>
				</div>
			</div>

			{/* Form Responses Section - Only show if review has answers */}
			{review.answers && review.answers.length > 0 && <FormAnswersSection answers={review.answers} />}

			{/* Consent Section */}
			<div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
				{/* Header */}
				<div className="flex items-start gap-3 p-6">
					<div className="w-10 h-10 bg-gray-100 rounded-full flex items-center justify-center flex-shrink-0">
						<svg className="w-5 h-5 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
							<path
								strokeLinecap="round"
								strokeLinejoin="round"
								strokeWidth={1.5}
								d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z"
							/>
						</svg>
					</div>
					<div>
						<h3 className="text-t-15 font-semibold text-primary">Consent & Usage</h3>
						<p className="text-t-13 text-tertiary mt-0.5">How this review can be used</p>
					</div>
				</div>

				{/* Content */}
				<div className="border-t border-gray-200 p-6">
					<div
						className={`inline-flex items-center gap-3 px-4 py-3 rounded-lg border ${
							review.consent === "public"
								? "border-green-500 bg-green-50 text-green-700"
								: review.consent === "anonymous"
									? "border-purple-500 bg-purple-50 text-purple-700"
									: "border-blue-500 bg-blue-50 text-blue-700"
						}`}
					>
						{review.consent === "public" ? (
							<svg className="w-5 h-5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
								<path
									strokeLinecap="round"
									strokeLinejoin="round"
									strokeWidth={1.5}
									d="M3.055 11H5a2 2 0 012 2v1a2 2 0 002 2 2 2 0 012 2v2.945M8 3.935V5.5A2.5 2.5 0 0010.5 8h.5a2 2 0 012 2 2 2 0 104 0 2 2 0 012-2h1.064M15 20.488V18a2 2 0 012-2h3.064M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
								/>
							</svg>
						) : review.consent === "anonymous" ? (
							<IconAnonymous className="w-5 h-5 flex-shrink-0" />
						) : (
							<svg className="w-5 h-5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
								<path
									strokeLinecap="round"
									strokeLinejoin="round"
									strokeWidth={1.5}
									d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"
								/>
							</svg>
						)}
						<div>
							<div className="font-medium text-sm">{review.consent === "public" ? "Share it publicly" : review.consent === "anonymous" ? "Share it anonymously" : "Keep it private"}</div>
							<div className="text-xs opacity-75">
								{review.consent === "public" || review.consent === "anonymous" ? "On your website, social media, and marketing materials" : "For internal use and feedback only"}
							</div>
						</div>
					</div>
				</div>
			</div>

			{/* Person Information Section */}
			<div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
				{/* Header */}
				<div className="flex items-start gap-3 p-6">
					<div className="w-10 h-10 bg-gray-100 rounded-full flex items-center justify-center flex-shrink-0">
						<svg className="w-5 h-5 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
							<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
						</svg>
					</div>
					<div>
						<h3 className="text-t-15 font-semibold text-primary">Person Information</h3>
						<p className="text-t-13 text-tertiary mt-0.5">Who gave this review</p>
					</div>
				</div>

				{/* Content */}
				<div className="border-t border-gray-200 p-6 space-y-5">
					{/* Avatar and Name Row */}
					<div className="flex items-center gap-4">
						{review.customer_avatar_url ? (
							<img src={review.customer_avatar_url} alt={review.customer_name} className="w-16 h-16 rounded-full object-cover border border-gray-200" />
						) : (
							<div className="w-16 h-16 rounded-full bg-brand/10 flex items-center justify-center text-brand font-semibold text-xl">{review.customer_name?.charAt(0).toUpperCase()}</div>
						)}
						<div>
							<div className="text-lg font-semibold text-primary">{review.customer_name}</div>
							{review.customer_job_title && <div className="text-sm text-tertiary">{review.customer_job_title}</div>}
						</div>
					</div>

					{/* Headline */}
					{review.customer_headline && <ReadOnlyField label="Headline" value={review.customer_headline} />}

					{/* Contact Info */}
					<div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
						{review.customer_email && (
							<div className="space-y-1.5">
								<label className="block text-xs font-medium text-tertiary uppercase tracking-wide">Email Address</label>
								<a href={`mailto:${review.customer_email}`} className="text-sm text-brand hover:underline">
									{review.customer_email}
								</a>
							</div>
						)}
						{review.customer_phone && (
							<div className="space-y-1.5">
								<label className="block text-xs font-medium text-tertiary uppercase tracking-wide">Phone Number</label>
								<a href={`tel:${review.customer_phone}`} className="text-sm text-brand hover:underline">
									{review.customer_phone}
								</a>
							</div>
						)}
					</div>
				</div>
			</div>

			{/* Company Information Section */}
			{(review.customer_company_name || review.customer_company_logo_url || review.customer_company_website_url) && (
				<div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
					{/* Header */}
					<div className="flex items-start gap-3 p-6">
						<div className="w-10 h-10 bg-gray-100 rounded-full flex items-center justify-center flex-shrink-0">
							<svg className="w-5 h-5 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
								<path
									strokeLinecap="round"
									strokeLinejoin="round"
									strokeWidth={1.5}
									d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4"
								/>
							</svg>
						</div>
						<div>
							<h3 className="text-t-15 font-semibold text-primary">Company Information</h3>
							<p className="text-t-13 text-tertiary mt-0.5">Where they work</p>
						</div>
					</div>

					{/* Content */}
					<div className="border-t border-gray-200 p-6 space-y-5">
						{/* Logo and Name Row */}
						<div className="flex items-center gap-4">
							{review.customer_company_logo_url ? (
								<img src={review.customer_company_logo_url} alt={review.customer_company_name} className="w-16 h-12 rounded-lg object-contain bg-gray-50 border border-gray-200 p-1" />
							) : (
								<div className="w-16 h-12 rounded-lg bg-gray-100 flex items-center justify-center">
									<svg className="w-6 h-6 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
										<path
											strokeLinecap="round"
											strokeLinejoin="round"
											strokeWidth={1.5}
											d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4"
										/>
									</svg>
								</div>
							)}
							<div className="text-lg font-semibold text-primary">{review.customer_company_name || "—"}</div>
						</div>

						{/* Website */}
						{review.customer_company_website_url && <ReadOnlyField label="Website / Social Link" value={review.customer_company_website_url} isLink />}
					</div>
				</div>
			)}

			{/* Tags Section */}
			{review.tags && review.tags.length > 0 && (
				<div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
					{/* Header */}
					<div className="flex items-start gap-3 p-6">
						<div className="w-10 h-10 bg-gray-100 rounded-full flex items-center justify-center flex-shrink-0">
							<svg className="w-5 h-5 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
								<path
									strokeLinecap="round"
									strokeLinejoin="round"
									strokeWidth={1.5}
									d="M7 7h.01M7 3h5c.512 0 1.024.195 1.414.586l7 7a2 2 0 010 2.828l-7 7a2 2 0 01-2.828 0l-7-7A1.994 1.994 0 013 12V7a4 4 0 014-4z"
								/>
							</svg>
						</div>
						<div>
							<h3 className="text-t-15 font-semibold text-primary">Tags</h3>
							<p className="text-t-13 text-tertiary mt-0.5">Organization tags for this review</p>
						</div>
					</div>

					{/* Content */}
					<div className="border-t border-gray-200 p-6">
						<div className="flex flex-wrap gap-2">
							{review.tags.map((tag) => {
								const colorStyles = getTagColorStyles(tag.color);
								return (
									<span key={tag.id} className={`inline-flex items-center px-3 py-1.5 rounded-lg text-sm ${colorStyles.bg} ${colorStyles.text} ${colorStyles.border || ""}`}>
										{tag.label}
									</span>
								);
							})}
						</div>
					</div>
				</div>
			)}

			{/* Spacer for sticky footer */}
			<div className="h-24" />
		</div>
	);
}
